<template>
  <ValidationProvider name="Coverholder" rules="required" v-slot="{ errors }">

  <AdvancedDropdown
    v-model="modelValue"
    v-bind="$attrs"
    :options="options"
    :disabled="isCompanyRoleName('Coverholder') || disabled"
    :can-add-new="false"
    size="sm"
    :class="{ 'is-invalid': !!errors.length }"
  >
    <template v-slot:label>
      Coverholder <span class="text-danger">*</span>
    </template>
    <template v-slot:button-content>
      <template v-if="!!selectedOption">
        {{ selectedOption.text }}
      </template>
      <template v-else>Select Coverholder</template>
    </template>

    <template v-slot:item-extra="item">
      <a
        v-if="item.isNew"
        :class="{
          'text-light': modelValue.includes(item.value),
          'text-danger': !modelValue.includes(item.value)
        }"
        role="button"
        @click.prevent.stop="() => handleRemoveNewCoverholder(item)"
      >
        <BaseIcon icon="x" tag="i" />
      </a>
    </template>

    <template v-if="!isSuperUser" v-slot:add-new="{ cancel }">
      <BDXNewCoverholderForm
        size="sm"
        @cancel="cancel"
        @success="
          (newCoverholder) =>
            handleNewCoverholderFormSuccess(newCoverholder, cancel)
        "
      />
    </template>

    </AdvancedDropdown>
      <div v-if="!!errors.length" class="invalid-feedback d-block">
        {{ errors[0] }}
      </div>
    </ValidationProvider>
  </template>

<script>
import { defineComponent, computed } from '@vue/composition-api'

import {
  useCurrentUser,
  useUserCompany,
  useRLSGroup
} from '@composables'

import { BaseIcon } from '@components/base'
import { AdvancedDropdown } from '@components/advanced'
import { BDXNewCoverholderForm } from '@components/bdx'
import { ValidationProvider } from 'vee-validate'
import { KEEP_AS_IS_OPTION } from '@constants'

export default defineComponent({
  name: 'BDXEditFormCoverholder',
  components: {
    BaseIcon,
    AdvancedDropdown,
    BDXNewCoverholderForm,
    ValidationProvider,
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    useKeepAsIsOption: Boolean,
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['input', 'created:tpa', 'removed:tpa'],
  setup(props, { emit }) {
    const { isSuperUser } = useCurrentUser()
    const { isRoleName: isCompanyRoleName } = useUserCompany()
    const { isFetching, rlsGroupItems, addRLSGroupItem, removeRLSGroupItem } =
      useRLSGroup('Coverholder')

    const modelValue = computed({
      get: () => props.value,
      set: (newValue) => emit('input', newValue)
    })

    const options = computed(() => {
      let options = rlsGroupItems.value
        .filter((rlsGroupItem) => {
          if (isSuperUser.value || rlsGroupItem.isNew) return true

          // Companies of the same type as the signed-in user are excluded;
          // every other type is selectable without requiring an RLS relationship.
          return !isCompanyRoleName('Coverholder')
        })
        .map((rlsGroupItem) => ({
          text: rlsGroupItem.coverholder,
          value: rlsGroupItem.id,
          ...(rlsGroupItem.isNew && { disabled: true, isNew: true })
        }))
        if (props.useKeepAsIsOption) {
          options = [KEEP_AS_IS_OPTION, ...options];
        }
        return options;
      }
    )

    const selectedOption = computed(() =>
      options.value.find((option) => modelValue.value.includes(option.value))
    )

    // New Coverholder form
    const handleRemoveNewCoverholder = (Coverholder) => {
      removeRLSGroupItem(Coverholder.value)

      emit('removed:coverholder', Coverholder)
    }

    const handleNewCoverholderFormSuccess = (
      newCoverholder,
      successCallback
    ) => {
      if (isSuperUser.value) return

      addRLSGroupItem(newCoverholder)

      emit('created:coverholder', newCoverholder)

      successCallback()
    }

    return {
      isSuperUser,
      isCompanyRoleName,

      modelValue,

      isFetching,
      options,
      selectedOption,

      handleRemoveNewCoverholder,
      handleNewCoverholderFormSuccess
    }
  }
})
</script>

<style scoped>
.advanced-dropdown >>> .dropdown-toggle {
  max-width: 214px;
}

.advanced-dropdown >>> .dropdown-menu .list-group-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.advanced-dropdown >>> .dropdown-menu .list-group-item.disabled {
  color: #fff;
  background-color: #1c4a50;
  border-color: #1c4a50;
  pointer-events: initial;
}
.advanced-dropdown.is-invalid >>> .form-control{
  border-color: #fb6340;
}
</style>
